import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Box from "Components/Atoms/Box";
import Typography from "Components/Atoms/Typography";
import Button from "Components/Atoms/Button";
import { Event, EventAddOn } from "types/calendar";
import Translations from "App/Settings/Components/Translations";
import Modal from "Components/Molecules/Modal";
import ImageField from "Components/Organisms/ImageField";
import TextField from "Components/Atoms/TextField";
import { Translation } from "utils/types";
import IconButton from "Components/Atoms/IconButton";
import { Close } from "Components/Atoms/Icons";
import useToast from "CustomHooks/useToast";
import { Severity } from "Contexts/RestaurantContext";
import AddOnBookingSettings from "./AddOnBookingSettings";
import useRestaurantVatCountry from "CustomHooks/useRestaurantVatCountry";
import useRestaurant from "CustomHooks/useRestaurant";
import { vatSplitSaveError } from "./vatSplitSaveError";

type EditAddonModalProps = {
  openEditOptions:
    | false
    | {
        isOpen: boolean;
        optId: string | null;
        type: "options" | "addons";
      };
  setOpenEditOptions: React.Dispatch<
    React.SetStateAction<
      | false
      | {
          isOpen: boolean;
          optId: string | null;
          type: "options" | "addons";
        }
    >
  >;
  setEventState: React.Dispatch<React.SetStateAction<Event | null>>;
  eventState: Event | null;
};

const EditAddonModal = ({
  openEditOptions,
  setOpenEditOptions,
  setEventState,
  eventState,
}: EditAddonModalProps) => {
  const toast = useToast();
  const { t } = useTranslation("calendar");
  const vatCountry = useRestaurantVatCountry();
  const { restaurantId } = useRestaurant();
  const [vatPending, setVatPending] = React.useState<string | null>(null);

  const originalAddon = useMemo(
    () =>
      eventState &&
      eventState.optionalAddOns &&
      openEditOptions &&
      [...eventState.optionalAddOns].filter(
        (o) => o.id === openEditOptions.optId
      )[0],
    [openEditOptions]
  );

  const modifyAddon = (toEditAddon: EventAddOn) => {
    if (eventState?.optionalAddOns && openEditOptions) {
      const filteredAddons = [...eventState?.optionalAddOns].filter(
        (o) => o.id !== openEditOptions.optId
      );

      setEventState({
        ...eventState,
        optionalAddOns: [...filteredAddons, toEditAddon],
      });
    }
  };

  const handleCancel = () => {
    if (eventState && eventState?.optionalAddOns) {
      if (originalAddon && originalAddon.id !== "newAddon") {
        let editedAddons: EventAddOn[] = [...eventState.optionalAddOns].filter(
          (o) => o.id !== originalAddon.id
        );

        setEventState({
          ...eventState,
          optionalAddOns: [...editedAddons, originalAddon],
        });
      } else {
        let deleteNewAddon: EventAddOn[] = eventState.optionalAddOns.filter(
          (o) => o.id !== "newAddon"
        );

        setEventState({ ...eventState, optionalAddOns: deleteNewAddon });
      }
      setOpenEditOptions(false);
    }
  };

  const handleSave = () => {
    if (eventState && eventState?.optionalAddOns) {
      const eventStateAddons = [...eventState.optionalAddOns];
      const newAddon: any =
        originalAddon &&
        eventStateAddons.find((o) => o.id === originalAddon.id);

      if (newAddon) {
        if (!newAddon.internalName) {
          return toast(
            "An internal name is required, please provide one to save your add-on.",
            Severity.WARNING,
            "common"
          );
        }

        if (
          newAddon.minGuests != null &&
          (!Number.isInteger(newAddon.minGuests) || newAddon.minGuests < 1)
        ) {
          return toast(
            "Minimum guests must be a positive integer",
            Severity.WARNING,
            "calendar"
          );
        }

        const vatError = vatSplitSaveError(
          newAddon,
          newAddon.amount ?? 0,
          vatCountry.country,
          vatPending,
          t
        );
        if (vatError) {
          // Already translated: no second lookup (names may contain "_").
          return toast(vatError, Severity.WARNING, null);
        }

        const toSaveAddon = {
          ...newAddon,
          id: newAddon.id === "newAddon" ? `${Date.now()}` : newAddon.id,
        };

        modifyAddon(toSaveAddon);
      }
      setOpenEditOptions(false);
    }
  };

  const handleNameChange = (name: string) => {
    if (eventState?.optionalAddOns && openEditOptions) {
      let toEditAddon: any = {
        ...eventState.optionalAddOns.find(
          (o) => o.id === openEditOptions.optId
        ),
      };

      toEditAddon.internalName = name;

      modifyAddon(toEditAddon);
    }
  };

  const handleAddonTitleAndDescriptionChange = (
    e: Record<string, Translation<string>>
  ) => {
    const newText: Record<
      string,
      {
        title: string;
        description: string;
      }
    > = Object.entries(e).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {}
    );

    if (eventState && eventState.optionalAddOns && openEditOptions) {
      let toEditAddon: any = {
        ...eventState.optionalAddOns.find(
          (o) => o.id === openEditOptions.optId
        ),
      };

      toEditAddon.translation = newText;

      modifyAddon(toEditAddon);
    }
  };

  const handleAddonImage = (e: string | (string | null)[] | null) => {
    if (eventState?.optionalAddOns && openEditOptions) {
      let toEditAddon: any = {
        ...eventState.optionalAddOns.find(
          (o) => o.id === openEditOptions.optId
        ),
      };

      if (typeof e === "string") toEditAddon.img = e;

      modifyAddon(toEditAddon);
    }
  };

  if (!eventState) {
    return <></>;
  }

  return (
    <Modal
      title="Optional Add Ons"
      maxWidth="md"
      fullWidth
      className="addon-editor"
      open={!!openEditOptions && openEditOptions.type === "addons"}
      headerRight={
        <Box flex style={{ justifyContent: "flex-end", width: "auto" }}>
          <Button
            variant="primary"
            onClick={handleSave}
            style={{ marginRight: 16 }}
            typographyProps={{ variant: "text-3" }}
          >
            Save
          </Button>
          <IconButton size="small" onClick={handleCancel}>
            <Close color="inherit" />
          </IconButton>
        </Box>
      }
    >
      {openEditOptions &&
        openEditOptions.type === "addons" &&
        eventState.optionalAddOns &&
        eventState.optionalAddOns.map((o) => {
          if (o.id === openEditOptions.optId) {
            return (
              <React.Fragment key={o.id}>
                <Box className="mg-bt-md">
                  <Typography variant="h5" translation="calendar">
                    Add-on details
                  </Typography>
                  <Typography
                    color="subdued"
                    variant="text-3"
                    translation="calendar"
                  >
                    Add a name for your team, then the title and description
                    your guests will see.
                  </Typography>
                </Box>

                <Box className="addon-editor__name">
                  <TextField
                    fullWidth
                    required
                    label="Choose an internal name"
                    helperText="Only visible to your team, not to guests."
                    helperTextTranslation="calendar"
                    value={o.internalName || ""}
                    onChange={(e) => handleNameChange(e.currentTarget.value)}
                  />
                </Box>

                <Box className="addon-editor__content">
                  <Box>
                    <Translations
                      borders
                      state={{
                        ...o.translation,
                        de: {
                          title: o.translation?.de?.title || "",
                          description: o.translation?.de?.description || "",
                        },
                        en: {
                          title: o.translation?.en?.title || "",
                          description: o.translation?.en?.description || "",
                        },
                      }}
                      onChange={handleAddonTitleAndDescriptionChange}
                      keys={[
                        {
                          id: "title",
                          label: "Title",
                          required: true,
                        },
                        {
                          id: "description",
                          label: "Description",
                          required: true,
                          multiline: true,
                        },
                      ]}
                    />
                  </Box>
                  <ImageField
                    value={o?.img || ""}
                    onChange={handleAddonImage}
                    label={""}
                    type="url"
                  />
                </Box>

                <AddOnBookingSettings
                  addOn={o}
                  onChange={modifyAddon}
                  vatCountry={vatCountry}
                  restaurantId={restaurantId ?? ""}
                  onVatPendingChange={setVatPending}
                />
              </React.Fragment>
            );
          }
        })}
    </Modal>
  );
};

export default EditAddonModal;
